import RoundTab from "components/RoundTabButtons/RoundTab";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import CreateProjectDropdown from "../../../components/DropdownStyle/CreateProjectDropdown";
import ComponentCircleLoader from "../../../components/Loaders/ComponentCircleLoader";
import Map from "../../../components/ReactGoogleMapsApi/GoogleMap";
import Select from "../../../components/ReactSelectDropdown";
import { useScrollShadow } from "../../../customHooks/useScrollShadow";
import { TabSearchGrid } from "../../../styledComponents/ProjectsNew";
import {
  TabButton,
  TabButtonContainer,
} from "../../../styledComponents/buttons";
import {
  CommonGrid,
  ContainerNew,
  FilterLabel,
  HeaderContainer,
  InputWithLabelSection,
  PageTitle,
} from "../../../styledComponents/common";
import { StickyFilterContainer } from "../../../styledComponents/timesheet";
import {
  MapAndAlertContainer,
  MapTopAlert,
} from "../RouteMapNew/routeMapStyles";
import { timezoneToCoords } from "utils/timeZoneCoordinates";

const LiveLocation = ({
  liveLocationList, // API: latest locations (live tab)
  getLatestLocationList, // action (live tab)
  awayLocationList, // API: latest locations (away tab)
  awayLocationListLoading, // loading flag (away tab)
  getAwayLocationList, // action (away tab)
  selectedOrganization, // current org
  liveLocationListLoading, // loading flag (live tab)
  getOfficesShortList, // action
  officesShortList, // office dropdown
  onlineMembers, // API: clocked-in users
  getAllMemberLastClockInOut, // action
}) => {
  const { t } = useTranslation();
  const showShadow = useScrollShadow(60);
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";

  // UI
  const [selectedTab, setSelectedTab] = useState("live");
  const [awayUsers, setAwayUsers] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [userCurrentLocation, setUserCurrentLocation] = useState(
    timezoneToCoords(
      localStorage.getItem("selected_organization_timezone") ||
        selectedOrganization?.timezone ||
        moment.tz.guess(),
    ),
  );

  // office filter
  const [selectedOffice, setSelectedOffice] = useState({
    value: null,
    label: "(All Teams)",
  });
  const [officeOptions, setOfficeOptions] = useState([]);

  const officeFilterOptions = [
    { value: null, label: "(All Teams)" },
    { value: "office_users", label: "(Office users)" },
    { value: "remote_users", label: "(Remote users)" },
    ...officeOptions,
  ];

  // ref so live-location effect always reads the current tab without being a dependency
  const selectedTabRef = useRef(selectedTab);
  useEffect(() => {
    selectedTabRef.current = selectedTab;
  }, [selectedTab]);

  const onTabSwitch = value => setSelectedTab(value);
  const selectOffice = e => setSelectedOffice(e);

  // detect own location
  // useEffect(() => {
  //   fetch("https://hutils.loxal.net/whois")
  //     .then(resp => resp.json())
  //     .then(res => {
  //       if (res && res.latitude && res.longitude) {
  //         setUserCurrentLocation({
  //           lat: res.latitude,
  //           lng: res.longitude,
  //         });
  //       }
  //     })
  //     .catch(() => {});
  // }, []);

  // fetch offices when org changes
  useEffect(() => {
    if (selectedOrganization?.id) {
      getOfficesShortList({ organization_id: selectedOrganization.id });
    }
  }, [selectedOrganization]);

  // always refresh online members on tab switch or org change (both tabs need it)
  useEffect(() => {
    if (selectedOrganization?.id) {
      getAllMemberLastClockInOut({ organization_id: selectedOrganization.id });
    }
  }, [selectedTab, selectedOrganization]);

  // LIVE tab: only fires when onlineMembers updates (after getAllMemberLastClockInOut succeeds)
  // selectedTab intentionally read via ref to avoid firing before the API returns
  useEffect(() => {
    if (
      selectedTabRef.current === "live" &&
      selectedOrganization?.id &&
      onlineMembers &&
      onlineMembers.length > 0
    ) {
      const minClockin = Math.min(
        ...onlineMembers.map(m => m.clockin_timestamp),
      );
      getLatestLocationList({
        organization_id: selectedOrganization.id,
        from: minClockin + 5,
        to: moment().endOf("day").unix(),
        officeFilter: selectedOffice?.value,
      });
    }
  }, [onlineMembers, selectedOffice, selectedOrganization]);

  // AWAY tab: fully independent call — no relation to onlineMembers
  useEffect(() => {
    if (selectedTab === "away" && selectedOrganization?.id) {
      getAwayLocationList({
        organization_id: selectedOrganization.id,
        from: moment().startOf("day").unix(),
        to: moment().endOf("day").unix(),
        officeFilter: selectedOffice?.value,
      });
    }
  }, [selectedTab, selectedOffice, selectedOrganization]);

  // prepare office dropdown
  useEffect(() => {
    if (officesShortList?.length > 0) {
      setSelectedOffice({ value: null, label: "(All Teams)" });
      const newArr = officesShortList.map(office => ({
        label: office.name,
        value: office.id,
      }));
      setOfficeOptions(newArr);
    }
  }, [officesShortList]);

  // LIVE tab: merge onlineMembers + liveLocationList → liveUsers
  useEffect(() => {
    if (!liveLocationList || !onlineMembers) return;

    const live = [];
    const now = moment();

    onlineMembers.forEach(user => {
      const locationUser = liveLocationList.find(
        loc => loc.employee.id === user.id,
      );

      const lat = parseFloat(
        locationUser?.latest_location?.lat || user.clockin_lat,
      );
      const lng = parseFloat(
        locationUser?.latest_location?.long || user.clockin_long,
      );

      if (!isFinite(lat) || !isFinite(lng)) return;

      if (locationUser) {
        live.push({
          ...locationUser,
          latest_location: {
            ...locationUser.latest_location,
            lat,
            long: lng,
          },
          from: Math.min(...onlineMembers.map(m => m.clockin_timestamp)),
          to: moment().endOf("day").unix(),
        });
      }
    });

    setLiveUsers(live);
  }, [liveLocationList, onlineMembers]);

  // AWAY tab: use awayLocationList, exclude anyone currently in onlineMembers
  useEffect(() => {
    if (!awayLocationList) return;

    const onlineIds = new Set((onlineMembers || []).map(m => m.id));
    const now = moment();
    const away = [];

    awayLocationList.forEach(locationUser => {
      const employeeId = locationUser?.employee?.id;

      // exclude online members
      if (onlineIds.has(employeeId)) return;

      const lat = parseFloat(locationUser?.latest_location?.lat);
      const lng = parseFloat(locationUser?.latest_location?.long);
      if (!isFinite(lat) || !isFinite(lng)) return;

      away.push({
        ...locationUser,
        latest_location: {
          ...locationUser.latest_location,
          lat,
          long: lng,
        },
        from: locationUser?.latest_location?.timestamp
          ? moment.unix(locationUser.latest_location.timestamp).unix()
          : now.unix(),
        to: now.unix(),
      });
    });

    setAwayUsers(away);
  }, [awayLocationList, onlineMembers]);

  return (
    <div className="content">
      <ContainerNew>
        <PageTitle>{t("live_location")}</PageTitle>

        {/* Sticky filters */}
        <StickyFilterContainer
          showShadow={showShadow}
          style={{
            width: "calc(100% + 40px)",
            marginLeft: "-20px",
            marginRight: "-20px",
            paddingLeft: "24px",
            paddingRight: "24px",
            marginBottom: "0px",
          }}
        >
          <HeaderContainer margin="0px">
            <TabSearchGrid columns="auto auto">
              {/* Tab filter */}
              <InputWithLabelSection>
                <FilterLabel>{t("type")}</FilterLabel>
                <CommonGrid columns="auto auto" gap="5px">
                  <RoundTab
                    text={t("live")}
                    selected={selectedTab === "live"}
                    onClick={() => onTabSwitch("live")}
                    showIcon={false}
                    tooltipEnabled={true}
                    tooltipText={t("to_see_your_online_members")}
                  />
                  <RoundTab
                    text={t("last_detected_position")}
                    selected={selectedTab === "away"}
                    onClick={() => onTabSwitch("away")}
                    showIcon={false}
                    tooltipEnabled={true}
                    tooltipText={t(
                      "to_see_the_location_where_your_employees_logged_out",
                    )}
                  />
                </CommonGrid>
                {/* <TabButtonContainer width="max-content" disableResponsive>
          <TabButton
            onClick={() => onTabSwitch("live")}
            selected={selectedTab}
            index="live"
          >
            {t("live")}
          </TabButton>
          <TabButton
            onClick={() => onTabSwitch("away")}
            selected={selectedTab}
            index="away"
          >
            {t("last_detected_position")}
          </TabButton>
        </TabButtonContainer> */}
              </InputWithLabelSection>

              {/* Office filter */}
              {isOperationType && ( // only show office filter for operation type
                <InputWithLabelSection style={{ minWidth: `240px` }}>
                  <FilterLabel>Team</FilterLabel>
                  <Select
                    isSearchable={false}
                    value={selectedOffice}
                    options={officeFilterOptions}
                    placeholder=""
                    onChange={e => selectOffice(e)}
                    styles={CreateProjectDropdown(null)}
                  />
                </InputWithLabelSection>
              )}
            </TabSearchGrid>
          </HeaderContainer>
        </StickyFilterContainer>

        {/* Map and Alerts */}
        <MapAndAlertContainer>
          {(
            selectedTab === "live"
              ? liveLocationListLoading
              : awayLocationListLoading
          ) ? (
            <MapTopAlert fontSize="15px" height="48px">
              <ComponentCircleLoader padding="10px 0" size={16} color="#fff" />
            </MapTopAlert>
          ) : (
            <>
              {/*Always show counts */}
              <MapTopAlert fontSize="15px" height="48px">
                {t("live")}: {liveUsers.length} | {t("away")}:{" "}
                {awayUsers.length}
              </MapTopAlert>

              {/*Conditional messages */}
              {selectedTab === "away" && awayUsers.length === 0 && (
                <MapTopAlert fontSize="15px" height="48px">
                  {t("no_one_is_offline_now")}
                </MapTopAlert>
              )}
              {selectedTab === "live" && liveUsers.length === 0 && (
                <MapTopAlert fontSize="15px" height="48px">
                  {t("no_one_is_live_now")}
                </MapTopAlert>
              )}
            </>
          )}

          {/* Map */}
          <Map
            userList={selectedTab === "away" ? awayUsers : liveUsers}
            isAwayUsers={selectedTab === "away"}
            userCurrentLocation={userCurrentLocation}
          />
        </MapAndAlertContainer>
      </ContainerNew>
    </div>
  );
};

export default LiveLocation;
